import type { AqeTokenConfig } from '@pushly/aqe'

/**
 * =============================================================================
 * HALOCLINE TOKEN CONFIGURATION
 * =============================================================================
 *
 * Halocline-specific token overrides that extend AQE defaults. Mirrors the
 * brand token shape used by pufferfish (pufferfish-tokens.config.ts) so the
 * two apps render with the same Pushly look and feel.
 *
 * Synchronized with the Pushly color palette defined in:
 *   - aqe/src/style/colors.scss (SCSS variables)
 *   - aqe/src/theme/tokens.config.ts (AQE defaults)
 *
 * TOKEN MERGE ORDER:
 * 1. AQE defaultTokens (base)
 * 2. halocineTokens (this file - overrides AQE)
 * 3. (future) white-label runtime overrides
 */
export const halocineTokens: Partial<AqeTokenConfig> = {
    token: {
        // =================================================================
        // COLOR SEED TOKENS - Synchronized with colors.scss
        // =================================================================

        // Primary brand color - used for buttons, switches, tabs, selections
        // Maps to: $color-secondary-100 (teal) - the interactive brand color
        colorPrimary: '#60C3AC',

        // Success color - completed, enabled, running states
        colorSuccess: '#60C3AC',

        // Warning color - pending, attention needed
        colorWarning: '#ffbf00',

        // Error color - failed, destructive actions
        colorError: '#f04134',

        // Link color - hyperlinks and clickable text
        colorLink: '#60C3AC',
        colorLinkHover: '#6fcfb8',

        // Base text color - derives all text color gradients
        colorTextBase: '#303030',
        colorTextSecondary: '#626262',

        // Base background color - derives all background gradients
        colorBgBase: '#ffffff',

        // =================================================================
        // ADDITIONAL DERIVED TOKENS
        // =================================================================

        colorBgContainer: '#ffffff',

        // =================================================================
        // TYPOGRAPHY TOKENS
        // =================================================================

        fontSize: 14,
        fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',

        // =================================================================
        // BORDER TOKENS
        // =================================================================

        borderRadius: 6,
        borderRadiusSM: 4,
        borderRadiusLG: 8,
    } as AqeTokenConfig['token'],

    // =========================================================================
    // COMPONENT-SPECIFIC TOKEN OVERRIDES
    // =========================================================================

    components: {
        // Button uses teal as primary color
        Button: {
            colorPrimary: '#60C3AC',
            primaryColor: '#ffffff',
            defaultGhostBorderColor: 'transparent',
        },

        // Menu selection states
        Menu: {
            darkItemBg: '#464d53', // $color-primary-100 for dark sidebar
            itemSelectedBg: 'rgba(96, 195, 172, 0.1)',
            itemSelectedColor: '#60C3AC',
            itemPaddingInline: '8px 16px',
            itemHeight: 28,
        },

        // Table header background
        Table: {
            headerBg: '#fbfafa',
        },

        // Tabs - brand teal for selected/active tab text
        Tabs: {
            inkBarColor: '#60C3AC',
        },

        // Switch uses teal when checked
        Switch: {
            colorPrimary: '#60C3AC',
            colorPrimaryHover: '#6fcfb8',
        },

        // Layout sidebar background
        Layout: {
            siderBg: '#464d53', // $color-primary-100
            triggerBg: '#464d53',
        },

        // Typography defaults
        Typography: {
            titleMarginBottom: 0,
        },

        // Form spacing. Antd v6's default is 24px; using 8px gives ghost-Well
        // subsections a compact, settings-page rhythm closer to pufferfish.
        // (Note: a global SCSS rule in antd-overrides.scss also sets 8px, but
        // antd's CSS-in-JS token wins, so this is the load-bearing knob.)
        Form: {
            itemMarginBottom: 8,
        },

        // Notification — compact layout for title-less simple notifications
        Notification: {
            paddingContentHorizontalLG: 16,
        },
    },
}
